// Client público (sem auth) do checkout de cobrança — Story 4.14.
import axios from 'axios';
import { z } from 'zod';

import { env } from './runtime-env';

const publicClient = axios.create({
  baseURL: env.apiUrl,
  withCredentials: false,
  timeout: 30_000,
});

// ----------------------------------------------------------------------------
// Schemas (Tier 2 — parse obrigatório no frontend)
// ----------------------------------------------------------------------------

const detailSchema = z.object({
  charge_id: z.string(),
  valor: z.string(),
  descricao: z.string(),
  cliente_nome: z.string(),
  status: z.string(),
  payment_method: z.string().nullable(),
  paid: z.boolean(),
  // SaaS da cobrança (2026-10-20) — escolhe o visual da página. Opcional: API
  // antiga sem o campo continua no visual padrão em vez de quebrar o parse.
  saas: z
    .object({ slug: z.string(), name: z.string(), url: z.string().nullable() })
    .nullable()
    .optional(),
  // Oferta "salvar este cartão para as próximas mensalidades" (29/10). Opcional
  // pelo mesmo motivo do `saas`: API antiga sem o campo = sem caixa.
  card_save_offer: z.object({ price: z.string(), cycle: z.string() }).nullable().optional(),
});
export type ChargeCheckoutDetail = z.infer<typeof detailSchema>;
export type CardSaveOffer = NonNullable<ChargeCheckoutDetail['card_save_offer']>;

const CYCLE_UNIT: Record<string, string> = { mensal: 'mês', trimestral: 'trimestre', semestral: 'semestre', anual: 'ano' };

/** "Salvar este cartão para as próximas mensalidades (R$ 99,00/mês)…" — texto ÚNICO das duas telas. */
export function cardSaveText(offer: CardSaveOffer): string {
  const price = Number(offer.price).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const unit = CYCLE_UNIT[offer.cycle];
  const amount = unit ? `${price}/${unit}` : price;
  return `Salvar este cartão para as próximas mensalidades (${amount}). A cobrança passa a ser automática e você pode cancelar quando quiser.`;
}

const payResultSchema = z.object({
  charge_id: z.string(),
  status: z.string(),
  payment_method: z.string(),
  paid: z.boolean(),
  payment_url: z.string().nullable(),
  pix_qr_image: z.string().nullable(),
  pix_payload: z.string().nullable(),
  pix_expiration: z.string().nullable(),
  bank_slip_url: z.string().nullable(),
  bank_slip_line: z.string().nullable(),
});
export type ChargePayResult = z.infer<typeof payResultSchema>;

const statusSchema = z.object({ status: z.string(), paid: z.boolean() });
export type ChargeCheckoutStatus = z.infer<typeof statusSchema>;

export interface ChargeCheckoutCreditCard {
  number: string;
  holder_name: string;
  expiry_month: string; // MM
  expiry_year: string; // YYYY
  ccv: string;
  postal_code?: string;
  address_number?: string;
}

export interface PayChargePayload {
  // Sem boleto: o servidor recusa desde 29/10 (boleto nunca em assinatura).
  payment_method: 'pix' | 'credit_card';
  credit_card?: ChargeCheckoutCreditCard;
  /** Só vai quando a tela mostrou a caixa (`card_save_offer`). */
  save_card?: boolean;
}

const envelope = <T extends z.ZodTypeAny>(schema: T) => z.object({ data: schema });

export async function fetchChargeCheckout(chargeId: string): Promise<ChargeCheckoutDetail> {
  const { data } = await publicClient.get(`/api/public/charge/${chargeId}`);
  return envelope(detailSchema).parse(data).data;
}

export async function payCharge(
  chargeId: string,
  payload: PayChargePayload,
): Promise<ChargePayResult> {
  const { data } = await publicClient.post(`/api/public/charge/${chargeId}/pay`, payload);
  return envelope(payResultSchema).parse(data).data;
}

export async function fetchChargeCheckoutStatus(chargeId: string): Promise<ChargeCheckoutStatus> {
  const { data } = await publicClient.get(`/api/public/charge/${chargeId}/status`);
  return envelope(statusSchema).parse(data).data;
}
